/**
 * `<AvatarImage>` — a pilot portrait `<img>` that **gracefully degrades to a fallback** when
 * the image fails to load (a stale `PilotData.image` whose file was removed from disk, a `404`,
 * a network error). Instead of the browser's broken-image icon, it renders the caller's
 * `fallback` (the callsign glyph in the picker, the striped "PILOT IMG" placeholder on the
 * license). The stored `image` is a server-relative URL (`/api/uploads/...`) usable directly as
 * `src` (dev proxies `/api`; prod/e2e are single-origin).
 *
 * Shared by the pilot picker tile, the license VIEW, and the editor preview so the image /
 * fallback / error behaviour can't drift between them.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

export function AvatarImage({
  src,
  alt,
  className,
  style,
  fallback,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  /** Rendered instead of a broken image when `src` fails to load (or is later re-broken). */
  fallback: ReactNode;
}) {
  const [broken, setBroken] = useState(false);
  // A re-upload swaps `src` to a fresh URL — clear the broken flag so the new image gets a try.
  useEffect(() => setBroken(false), [src]);
  if (broken) return <>{fallback}</>;
  return <img src={src} alt={alt} className={className} style={style} onError={() => setBroken(true)} />;
}
